import { useState } from "react";
import { ChevronDown, ClipboardList, MessageSquare } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import {
  QUESTIONNAIRE_CONFIGS,
  type QuestionnaireType,
} from "@/data/welcomeQuestionnaireData";

interface QuestionnaireViewerProps {
  questionnaireType: string;
  responses: Record<string, any>;
  additionalNotes?: string | null;
  defaultOpen?: boolean;
}

function formatResponseValue(question: any, value: any, detailValue?: string): string {
  if (value === undefined || value === null || value === "") return "Non renseigné";
  if (question.type === "checkbox" && Array.isArray(value)) {
    if (value.length === 0) return "Non renseigné";
    return value
      .map((v) => question.options?.find((opt: any) => opt.id === v)?.label || v)
      .join(", ");
  }
  if (question.type === "scale") return `${value}/10`;
  if (question.type === "conditionalText") {
    const selectedLabel = question.options?.find((opt: any) => opt.id === value)?.label || value;
    if (value === "oui" && detailValue) return `${selectedLabel} - ${detailValue}`;
    return selectedLabel;
  }
  if (question.type === "date" && typeof value === "object" && value.day && value.month && value.year) {
    return `${String(value.day).padStart(2, "0")}/${String(value.month).padStart(2, "0")}/${value.year}`;
  }
  return String(value);
}

export function QuestionnaireViewer({
  questionnaireType,
  responses,
  additionalNotes,
  defaultOpen = false,
}: QuestionnaireViewerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const config =
    QUESTIONNAIRE_CONFIGS[questionnaireType as QuestionnaireType] ||
    QUESTIONNAIRE_CONFIGS.default;

  const filledCount = Object.entries(responses || {}).filter(([key, value]) => {
    if (key.endsWith("_detail")) return false;
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  }).length;

  const hasNotes = !!(additionalNotes && additionalNotes.trim().length > 0);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border rounded-lg bg-muted/30">
      <CollapsibleTrigger className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition rounded-lg">
        <div className="flex items-center gap-2 text-left">
          <ClipboardList className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Questionnaire de bienvenue</span>
          <Badge variant="outline" className="text-[10px]">
            {filledCount} réponse{filledCount > 1 ? "s" : ""}
          </Badge>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-3 pt-0 space-y-3 border-t">
          <div className="grid gap-2 mt-3">
            {config.questions.map((question) => {
              const value = responses?.[question.id];
              const detailValue = responses?.[`${question.id}_detail`];

              if (question.id.endsWith("_autre") && !value) return null;
              if (question.id.endsWith("_autre")) {
                const parentId = question.id.replace("_autre", "");
                const parentValue = responses?.[parentId];
                if (!Array.isArray(parentValue) || !parentValue.includes("autre")) return null;
              }

              const displayValue = formatResponseValue(question, value, detailValue);
              if (displayValue === "Non renseigné") return null;

              return (
                <div key={question.id} className="text-sm">
                  <div className="font-medium text-foreground/80">{question.label}</div>
                  <div className="text-muted-foreground whitespace-pre-wrap">{displayValue}</div>
                </div>
              );
            })}
          </div>

          {hasNotes && (
            <div className="border-t pt-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground/80 mb-1">
                <MessageSquare className="w-4 h-4 text-primary" />
                Notes additionnelles
              </div>
              <div className="text-sm text-muted-foreground whitespace-pre-wrap bg-background p-2 rounded">
                {additionalNotes}
              </div>
            </div>
          )}

          {filledCount === 0 && !hasNotes && (
            <div className="text-sm text-muted-foreground italic">
              Aucune réponse enregistrée.
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
